import GravityAdapter from 'district-ui-client/adapters/gravity'
import { tracked } from '@glimmer/tracking'
import BulkManager from 'district-ui-client/utils/bulk-manager'
import { createRecordBulk, updateRecordBulk, deleteRecordBulk } from 'district-ui-client/utils/bulk-adapter-utils'
import type { Snapshot } from '@ember-data/legacy-compat/legacy-network-handler/snapshot'

/**
 * Same as the gravity adapter, but with the ability to batch create / update / delete requests.
 *
 * Use 'bulk: true' in the adapter options when creating a record.
 */
export default class GravityBulkAdapter extends GravityAdapter {
  @tracked _bulkManagerCreate: Nullable<BulkManager> = null

  @tracked _bulkManagerUpdate: Nullable<BulkManager> = null

  @tracked _bulkManagerDelete: Nullable<BulkManager> = null

  buildBulkManager(snapshot: Snapshot) {
    // use the same endpoint for creates, updates, deletes..
    const urlForBulkAction = this.urlForCreateRecord(snapshot.modelName, snapshot)
    // Note, this references the ember-data adapter "ajax" method, rather than an ajax service injection
    const adapterAjaxFn = this.ajax.bind(this)
    // BulkManager uses ember-concurrency to support doing these in batches
    return new BulkManager(adapterAjaxFn, urlForBulkAction)
  }

  createRecord(...args: Parameters<GravityAdapter['createRecord']>) {
    const [store, type, snapshot, ...otherArgs] = args
    if (snapshot?.adapterOptions?.bulk) {
      if (!this._bulkManagerCreate) this._bulkManagerCreate = this.buildBulkManager(snapshot)
      return createRecordBulk(snapshot, this._bulkManagerCreate)
    } else {
      return super.createRecord(store, type, snapshot, ...otherArgs)
    }
  }

  updateRecord(...args: Parameters<GravityAdapter['updateRecord']>) {
    const [store, type, snapshot, ...otherArgs] = args
    if (snapshot?.adapterOptions?.bulk) {
      if (!this._bulkManagerUpdate) this._bulkManagerUpdate = this.buildBulkManager(snapshot)
      return updateRecordBulk(store, type, snapshot, this._bulkManagerUpdate)
    } else {
      return super.updateRecord(store, type, snapshot, ...otherArgs)
    }
  }

  deleteRecord(...args: Parameters<GravityAdapter['deleteRecord']>) {
    const [store, type, snapshot, ...otherArgs] = args
    if (snapshot?.adapterOptions?.bulk) {
      if (!this._bulkManagerDelete) this._bulkManagerDelete = this.buildBulkManager(snapshot)
      return deleteRecordBulk(store, type, snapshot, this._bulkManagerDelete)
    } else {
      return super.deleteRecord(store, type, snapshot, ...otherArgs)
    }
  }
}
